/**
 * ResultScene — 终局庆典（卷十一 · 结果页规范）。
 * 棋局已定：胜方的十颗星子静静停在归营的星角里，
 * 奖牌以胜方颜色点亮，统计与入口克制呈现——庆典在棋盘上，不在弹窗里。
 */
import { CAMP_POSITIONS, destinationCamp } from '@shared/constants/camps';
import { MARBLE_RADIUS, PLAYER_COLORS } from '@shared/constants/board';
import type { ThemePreset } from '@shared/config/theme';
import type { HexPos } from '@shared/types/game';
import { hexKey } from '@shared/utils/hexMath';
import type { AppContext, Scene } from './context';
import type { MatchResult } from '../store/gameStore';
import { $matchResult } from '../store/gameStore';
import { buildBoard, type BoardHandle } from '../core/BoardBuilder';
import { createMarble, type MarbleMesh } from '../core/MarbleFactory';
import { $settings, $theme } from '../store/settingsStore';
import { netClient } from '../net/netClient';
import { resultInvite, share } from '../share/share';
import { tr, trf, type DictKey } from '../ui/i18n';

export class ResultScene implements Scene {
  private ctx!: AppContext;
  private board!: BoardHandle;
  private marbles: MarbleMesh[] = [];
  private overlay: HTMLElement | null = null;
  private theme!: ThemePreset;
  private winnerSeat: number | null = null;
  private reduceMotion = false;
  private unsubs: (() => void)[] = [];

  // ── Lifecycle ───────────────────────────────────────────
  enter(ctx: AppContext): void {
    this.ctx = ctx;
    // 没有可庆祝的终局（深链 / 热重载）→ 安静回到主页。
    const result = $matchResult.get();
    if (!result) {
      ctx.goTo('title');
      return;
    }
    this.winnerSeat = result.winnerSeat;
    this.theme = $theme.get();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.buildStage();
    const rig = ctx.engine.getRig();
    rig.view(Math.PI * 0.72, 0.9, 38);
    if (!this.reduceMotion) rig.setAutoRotate(0.035);

    ctx.hud.setTurn(null);
    ctx.hud.setChain(null);
    ctx.hud.setPlayers([]);
    ctx.hud.setHint(null);

    this.buildOverlay(result);
    this.bindSettings();
  }

  update(dt: number, elapsed: number): void {
    this.board.update(dt, elapsed);
    if (this.reduceMotion) return;
    // 呼吸律：归营的星子同频微光（克制律：低存在感）。
    const glow = 0.26 + Math.sin(elapsed * 2.4) * 0.1;
    for (const m of this.marbles) m.setEmissive(glow);
  }

  onThemeChange(theme: ThemePreset): void {
    this.theme = theme;
    this.buildStage();
    if (this.reduceMotion) {
      for (const m of this.marbles) m.setEmissive(0.18);
    }
  }

  exit(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.clearStage();
    this.overlay?.remove();
    this.overlay = null;
    this.ctx.engine.getRig().setAutoRotate(0);
  }

  // ── Stage ───────────────────────────────────────────────
  private buildStage(): void {
    this.clearStage();
    const scene = this.ctx.engine.getScene();

    this.board = buildBoard(this.theme);
    scene.add(this.board.group);
    this.ctx.engine.registerHoles(this.board.holeWorld, this.board.feltY);

    // 胜方十子归营的定格：放在其目的营（对角星角）里。
    // 联机终止局（winnerSeat=null）不摆庆典，只留空棋盘。
    if (this.winnerSeat !== null) {
      const dest = destinationCamp(this.winnerSeat);
      const iridescence = $settings.get().marbleIridescence;
      for (const pos of CAMP_POSITIONS[dest] ?? []) {
        this.spawnMarble(this.winnerSeat, pos, iridescence, scene);
      }
    }
    this.ctx.engine.registerPickables([]);
    if (this.reduceMotion) {
      for (const m of this.marbles) m.setEmissive(0.18);
    }
  }

  private spawnMarble(
    seat: number,
    pos: HexPos,
    iridescence: number,
    scene: import('three').Scene
  ): void {
    const key = hexKey(pos);
    const hole = this.board.holeWorld.get(key);
    if (!hole) return;
    const m = createMarble({
      seat,
      pos,
      key,
      color: PLAYER_COLORS[seat] ?? '#E63946',
      iridescence,
    });
    m.mesh.position.set(hole.x, hole.y + MARBLE_RADIUS, hole.z);
    scene.add(m.mesh);
    this.marbles.push(m);
  }

  private clearStage(): void {
    if (!this.ctx) return;
    const scene = this.ctx.engine.getScene();
    for (const m of this.marbles) {
      scene.remove(m.mesh);
      m.material.dispose();
    }
    this.marbles = [];
    if (this.board) {
      scene.remove(this.board.group);
      this.board.dispose();
    }
  }

  // ── Overlay ─────────────────────────────────────────────
  private buildOverlay(result: MatchResult): void {
    const root = document.createElement('div');
    root.className = 'overlay';
    this.overlay = root;

    const lang = $settings.get().language;
    const t = (k: DictKey) => tr(k, lang);

    // 奖牌：core 沿用 currentColor → 以胜方颜色点亮。
    const medal = document.createElement('div');
    medal.className = 'medal';
    medal.style.color = result.winnerColor;
    const core = document.createElement('div');
    core.className = 'core';
    medal.appendChild(core);

    // 胜负判词：人机/联机按本家座位判（你胜/惜败），同屏局给胜者全句；
    // 联机终止局（无胜者）给中性文案。
    const verdict =
      result.winnerSeat === null
        ? t('netAborted')
        : result.mode === 'ai'
          ? result.winnerSeat === 0
            ? t('win')
            : t('defeat')
          : result.mode === 'online'
            ? result.winnerSeat === result.yourSeat
              ? t('win')
              : t('defeat')
            : `${result.winnerName} ${t('winnerIs')}`;

    const title = document.createElement('h1');
    title.className = 'title-word';
    title.textContent = verdict;

    const sub = document.createElement('p');
    sub.className = 'subtitle';
    sub.textContent = `${t('players')} ${result.seats.length} · ${
      result.mode === 'ai'
        ? t('modeAi')
        : result.mode === 'online'
          ? t('netTitle')
          : result.mode === 'team'
            ? t('teamEntry')
            : t('modeHotseat')
    }`;

    const rule = document.createElement('div');
    rule.className = 'rule-line';

    const stats = document.createElement('div');
    stats.className = 'stat-row';
    stats.append(
      this.stat(String(result.moves), t('moves')),
      this.stat(String(result.rounds), t('turns'))
    );

    // 联机：服务端最终排名（含归营数 / 认输标记）。
    const standingsEl =
      result.mode === 'online' && result.standings && result.standings.length > 0
        ? this.buildStandings(result)
        : null;
    // v2.1：combo 战绩（本地 result.combos / 联机 standings 的服务端字段）。
    const comboEl = this.buildComboRows(result);

    const row = document.createElement('div');
    row.className = 'btn-row';
    const again = document.createElement('button');
    again.className = 'btn btn-primary';
    again.textContent = t('again');
    if (result.mode === 'online') {
      // 联机"再来一局" = REMATCH 投票：全会一致才开新房（服务端 30s 窗口，
      // 新房 MATCH_FOUND 由 main 的 $net 订阅自动导航进对局）。
      again.addEventListener('click', () => {
        void this.ctx.audio.init();
        netClient.voteRematch();
        again.disabled = true;
        again.textContent = t('netRematchWait');
      });
      this.bindRematch(again);
    } else {
      again.addEventListener('click', () => {
        void this.ctx.audio.init();
        this.ctx.goTo('game');
      });
    }
    const home = document.createElement('button');
    home.className = 'btn';
    home.textContent = t('backHome');
    home.addEventListener('click', () => {
      void this.ctx.audio.init();
      // 联机终局回主页前先退出房间（终局态 LEAVE_GAME 非认输）。
      if (result.mode === 'online') netClient.leaveGame();
      this.ctx.goTo('title');
    });
    // v2.1（SCREENS §13.1）：战绩分享——"我的"连跳/表现分按模式取数；
    // 联机终止局（无胜者）不提供战绩分享入口。
    if (result.winnerSeat !== null) {
      const shareBtn = document.createElement('button');
      shareBtn.className = 'btn';
      shareBtn.textContent = t('share');
      shareBtn.addEventListener('click', () => {
        void this.ctx.audio.init();
        const mine = this.myComboStats(result);
        share(
          resultInvite({
            players: result.seats.length,
            winnerName: result.winnerName,
            maxCombo: mine.maxCombo,
            performance: mine.performance,
          }),
          this.ctx.overlay,
          (msg) => this.ctx.hud.toast(msg)
        );
      });
      row.append(again, shareBtn, home);
    } else {
      row.append(again, home);
    }

    root.append(medal, title, sub, rule, stats);
    if (standingsEl) root.appendChild(standingsEl);
    if (comboEl) root.appendChild(comboEl);
    root.appendChild(row);
    this.ctx.overlay.appendChild(root);
  }

  /** v2.1：combo 战绩区块（RULES §5.2 四指标；全场最高 maxCombo 加 BEST COMBO 丝带）。 */
  private buildComboRows(result: MatchResult): HTMLElement | null {
    const lang = $settings.get().language;
    interface ComboRow {
      seat: number;
      name: string;
      maxCombo: number;
      totalHops: number;
      comboCount: number;
      performance: number;
    }
    let rows: ComboRow[] = [];
    if (result.mode === 'online') {
      // 联机：standings 的 v3.1 可选字段（旧服务端不下发 → 整块隐藏）。
      rows = (result.standings ?? [])
        .filter((st) => st.maxCombo !== undefined)
        .map((st) => ({
          seat: st.seat,
          name:
            (st.seat === result.yourSeat ? `${st.nickname}（${tr('you', lang)}）` : st.nickname) +
            (st.isAi ? ` · ${tr('ai', lang)}` : ''),
          maxCombo: st.maxCombo ?? 0,
          totalHops: st.totalHops ?? 0,
          comboCount: st.comboCount ?? 0,
          performance: st.performance ?? 0,
        }));
    } else if (result.combos) {
      rows = result.combos.map((c) => ({
        seat: c.seat,
        name:
          result.mode === 'ai'
            ? c.seat === 0
              ? tr('you', lang)
              : tr('ai', lang)
            : `P${result.seats.indexOf(c.seat) + 1}`,
        maxCombo: c.maxCombo,
        totalHops: c.totalHops,
        comboCount: c.comboCount,
        performance: c.performance,
      }));
    }
    // 无数据源或全局面零跳 → 不占位。
    if (rows.length === 0 || rows.every((r) => r.totalHops === 0)) return null;

    const best = Math.max(...rows.map((r) => r.maxCombo));
    let bestGiven = false;
    const box = document.createElement('div');
    box.className = 'combo-rows';
    const head = document.createElement('div');
    head.className = 'sec-title';
    head.textContent = tr('comboTitle', lang);
    box.appendChild(head);
    for (const r of rows) {
      const rowEl = document.createElement('div');
      rowEl.className = 'combo-row';
      const dot = document.createElement('span');
      dot.className = 'standing-dot';
      dot.style.background = PLAYER_COLORS[r.seat] ?? '#E63946';
      const name = document.createElement('span');
      name.className = 'standing-name';
      name.textContent = r.name;
      const meta = document.createElement('span');
      meta.className = 'combo-meta';
      meta.textContent = trf('comboRow', lang, {
        max: r.maxCombo,
        hops: r.totalHops,
        chains: r.comboCount,
        pts: r.performance,
      });
      rowEl.append(dot, name, meta);
      // 唯一一条 BEST COMBO 丝带：combo ≥ 2 才称得上"连跳组合"。
      if (!bestGiven && best >= 2 && r.maxCombo === best) {
        bestGiven = true;
        const ribbon = document.createElement('span');
        ribbon.className = 'combo-best';
        ribbon.textContent = tr('comboBest', lang);
        rowEl.appendChild(ribbon);
      }
      box.appendChild(rowEl);
    }
    return box;
  }

  /** v2.1：分享文案用的"我的"combo 数据（联机=本家座位；人机=seat 0；同屏=胜者座位）。 */
  private myComboStats(result: MatchResult): { maxCombo: number; performance: number } {
    if (result.mode === 'online') {
      const st = result.standings?.find((s) => s.seat === result.yourSeat);
      return { maxCombo: st?.maxCombo ?? 0, performance: st?.performance ?? 0 };
    }
    const seat = result.mode === 'ai' ? 0 : (result.winnerSeat ?? result.seats[0] ?? 0);
    const c = result.combos?.find((row) => row.seat === seat);
    return { maxCombo: c?.maxCombo ?? 0, performance: c?.performance ?? 0 };
  }

  /** 联机最终排名列表（rank · 座位色 · 昵称 · 归营数/认输）。 */
  private buildStandings(result: MatchResult): HTMLElement {
    const lang = $settings.get().language;
    const box = document.createElement('div');
    box.className = 'standings';
    const head = document.createElement('div');
    head.className = 'sec-title';
    head.textContent = tr('netStandings', lang);
    box.appendChild(head);
    for (const st of result.standings ?? []) {
      const rowEl = document.createElement('div');
      rowEl.className = 'standing-row';
      const rank = document.createElement('span');
      rank.className = 'standing-rank';
      rank.textContent = String(st.rank);
      const dot = document.createElement('span');
      dot.className = 'standing-dot';
      dot.style.background = PLAYER_COLORS[st.seat] ?? '#E63946';
      const name = document.createElement('span');
      name.className = 'standing-name';
      name.textContent =
        st.seat === result.yourSeat ? `${st.nickname}（${tr('you', lang)}）` : st.nickname;
      const meta = document.createElement('span');
      meta.className = 'standing-meta';
      meta.textContent = st.resigned ? tr('netResignedTag', lang) : `${st.marblesInDestination}/10`;
      rowEl.append(rank, dot, name, meta);
      box.appendChild(rowEl);
    }
    return box;
  }

  /** REMATCH 投票进度 → 按钮文案；房间关闭（窗口期结束）→ 锁定按钮。 */
  private bindRematch(again: HTMLButtonElement): void {
    this.unsubs.push(
      netClient.on('REMATCH_UPDATE', (p) => {
        if (!again.disabled) return; // 本家尚未投票，不打扰
        again.textContent = trf('netRematchVotes', $settings.get().language, {
          v: p.voters.length,
          n: p.needed.length,
        });
      }),
      netClient.on('ROOM_CLOSED', () => {
        again.disabled = true;
        again.textContent = tr('netRematchFailed', $settings.get().language);
      })
    );
  }

  private stat(num: string, lbl: string): HTMLElement {
    const box = document.createElement('div');
    box.className = 'stat';
    const n = document.createElement('div');
    n.className = 'num';
    n.textContent = num;
    const l = document.createElement('div');
    l.className = 'lbl';
    l.textContent = lbl;
    box.append(n, l);
    return box;
  }

  // ── Live settings ───────────────────────────────────────
  private bindSettings(): void {
    // 主题变更由 main 统一走 onThemeChange → buildStage，这里只跟随虹彩参数。
    this.unsubs.push(
      $settings.subscribe((s) => {
        for (const m of this.marbles) m.material.iridescence = s.marbleIridescence;
      })
    );
  }
}
